import Hue from '@assets/hue_yellow.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IPricingData } from '@models/IPricingData';
import { IProductColour } from '@models/IProductColour';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductTrackingData } from '@models/IProductTrackingData';
import { IProductVariant } from '@models/IProductVariant';
import ColourSwatch from '@molecules/ColourSwatch/ColourSwatch';
import Tags from '@molecules/Tags/Tags';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  readonly active?: boolean;
  readonly buyNowLink: ILink;
  readonly colours?: IProductColour[];
  readonly dataTestId?: string;
  readonly fromPriceLabel?: string;
  readonly hidePriceFromSavePill: boolean;
  readonly isLightMode?: boolean;
  readonly kitAvailableLabel?: string;
  readonly kitsAvailableLabel?: string;
  readonly learnMoreLink: ILink;
  readonly productListingName?: string;
  readonly newProductLabel?: string;
  readonly pricingData: IPricingData;
  readonly savePriceLabel: string;
  readonly shippingLabel: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly thumbnail: IImage;
  readonly title: string;
  readonly trackingData?: IProductTrackingData;
  readonly onKitDropdownButtonClick?: () => void;
  readonly variations?: IProductVariant[];
  readonly vatLabel: string;
}

const ProductListingCard = ({
  buyNowLink,
  colours,
  fromPriceLabel,
  hidePriceFromSavePill,
  kitAvailableLabel,
  kitsAvailableLabel,
  learnMoreLink,
  productListingName,
  newProductLabel,
  pricingData,
  savePriceLabel,
  shippingLabel,
  statusLabels,
  thumbnail,
  title,
  trackingData,
  variations,
  vatLabel,
  active,
  onKitDropdownButtonClick,
  isLightMode,
  dataTestId = 'Product-Listing-Card',
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const isXLarge = useMediaQuery(1440);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);
  const ctaBox = useRef<HTMLDivElement>(null);
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  const handleIntentToBuyEvent = (contentType: string) => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'intentToBuy',
      content_type: contentType,
      items: [
        mapToNewTrackingData(trackingData, { listingName: productListingName }),
      ],
    });
  };

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [
          mapToNewTrackingData(trackingData, {
            listingName: productListingName,
          }),
        ],
      },
    });
  };

  useEffect(() => {
    if (ctaBox.current) setCtaBoxHeight(ctaBox.current.clientHeight);
  }, []);

  const contVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const titleVariants = {
    hover: { y: -49 },
    initial: { y: 0 },
  };

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isXLarge ? 1 : 0 },
  };

  const hueVariants = {
    hover: { opacity: 0.2 },
    initial: { opacity: 0 },
  };

  const imgVariants = {
    hover: { y: -15, scale: 0.9 },
    inActive: { y: 0, scale: 1 },
  };

  const kitsVariationNumber =
    variations?.filter((variation) => variation.type === 'kit').length ?? 0;
  const kitButtonLabel =
    kitsVariationNumber > 1
      ? `${kitsVariationNumber} ${kitsAvailableLabel}`
      : `${kitsVariationNumber} ${kitAvailableLabel}`;

  return (
    <div
      track-product-impression="true"
      data-product={trackingDataJson}
      className={`relative h-full w-full ${
        isLightMode ? 'text-black-100' : 'text-white'
      }`}
      data-testid={dataTestId}
    >
      <div
        className={classNames(
          'absolute top-full left-0 h-4 w-full',
          active ? 'flex' : 'hidden',
          isLightMode ? 'bg-grey-100' : 'bg-grey-600'
        )}
      />

      <div
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={classNames(
          'pointer-events-auto flex h-full w-full gap-5 overflow-hidden p-5 lg:p-4 xl:p-5',
          active ? 'rounded-t-[10px]' : 'rounded-[10px]',
          isLightMode ? 'bg-grey-100' : 'bg-grey-600'
        )}
      >
        <div className="relative flex h-full w-full flex-col items-center justify-center">
          <div className="absolute top-0 left-0 z-20 flex w-full flex-row flex-wrap gap-4">
            <Tags
              hidePriceFromSavePill={hidePriceFromSavePill}
              pricingData={pricingData}
              savePriceLabel={savePriceLabel}
              newProductLabel={newProductLabel}
              statusLabels={statusLabels}
              dataTestId={`${dataTestId}-Tag`}
            />
          </div>

          <motion.div
            initial="initial"
            className="relative mt-10 flex h-[214px] w-full shrink-0 items-center justify-center lg:h-[200px] xl:h-[222px] 2xl:h-[302px]"
            animate={(active || isHovered) && !isXLarge ? 'hover' : 'initial'}
            variants={imgVariants}
            transition={easeInOut}
          >
            {thumbnail && (
              <a
                href={learnMoreLink?.url || buyNowLink?.url}
                onClick={() => {
                  if (learnMoreLink?.url)
                    handleIntentToBuyEvent('product picture');
                  else if (buyNowLink?.url) handleSelectItemEvent();
                }}
                aria-label={title}
                className="flex h-full w-full"
              >
                <Image
                  image={thumbnail}
                  classes="z-10 w-full h-full"
                  imageClasses="w-full h-full object-contain"
                  sizes="(max-width: 1024px) 376px, 500px"
                  dataTestId={`${dataTestId}-Image`}
                />

                <motion.div
                  initial="initial"
                  animate={
                    (active || isHovered) && !isXLarge ? 'hover' : 'initial'
                  }
                  variants={hueVariants}
                  className="pointer-events-none absolute top-10 left-0 z-0 flex h-full scale-150 items-center justify-center"
                >
                  <img
                    src={`${Hue}`}
                    alt="hue"
                    className="w-full object-contain"
                  />
                </motion.div>
              </a>
            )}
          </motion.div>

          <motion.div
            initial="initial"
            animate={(active || isHovered) && !isXLarge ? 'hover' : 'initial'}
            variants={titleVariants}
            transition={easeInOut}
            className="relative mt-15 mb-13 flex w-full flex-col items-center justify-center px-10 lg:mt-10 lg:mb-7.5 lg:px-2.5 xl:mt-20 xl:mb-24 xl:px-10"
          >
            <div className="-top-10 -translate-x-1/2 lg:-top-7.5 xl:-top-10 absolute left-1/2 flex">
              <ColourSwatch small displayTags variations={colours} />
            </div>

            {title && (
              <p
                className="h5 max-lg:text-center lg:w-full xl:w-auto xl:text-center"
                data-testid={`${dataTestId}-Title`}
              >
                {title}
              </p>
            )}
          </motion.div>

          <div className="relative w-full px-2.5 pb-2.5 lg:pb-0 xl:pb-2.5">
            <motion.div
              initial="initial"
              animate={(active || isHovered) && !isXLarge ? 'hover' : 'initial'}
              variants={contVariants}
              transition={easeInOut}
              className="relative flex w-full flex-col items-start justify-between gap-3 xl:flex-row xl:items-end"
            >
              <div className="flex flex-col gap-3 md:m-0">
                {pricingData?.formattedCalculatedPrice && (
                  <div className="flex flex-col">
                    <PriceDisplayText
                      pricingData={pricingData}
                      fromPriceLabel={fromPriceLabel}
                      vatLabel={vatLabel}
                      shippingLabel={shippingLabel}
                      isLightMode={isLightMode}
                      dataTestId={`${dataTestId}-Price`}
                    />
                  </div>
                )}

                {kitsVariationNumber > 0 && (
                  <div>
                    <LoadMoreButton
                      open={active}
                      _type={isLightMode ? 'BASIC' : 'YELLOW-HIGHLIGHT'}
                      loadMoreLabel={kitButtonLabel}
                      loadLessLabel={kitButtonLabel}
                      onClick={onKitDropdownButtonClick}
                      isLightMode={isLightMode}
                    />
                  </div>
                )}
              </div>

              <motion.div
                initial="initial"
                animate={
                  (active || isHovered) && !isXLarge ? 'hover' : 'initial'
                }
                variants={ctaVariants}
                transition={{
                  duration: 0.3,
                }}
                ref={ctaBox}
                className="flex w-full flex-row flex-wrap items-start justify-start gap-5 pt-5 lg:gap-2.5 lg:pt-2.5 xl:absolute xl:top-full xl:pt-5"
              >
                {learnMoreLink?.url && learnMoreLink?.name && (
                  <Button
                    link={learnMoreLink}
                    secondary
                    linkCallback={() =>
                      handleIntentToBuyEvent(learnMoreLink.name || 'Learn more')
                    }
                    isLightMode={isLightMode}
                    dataTestId={`${dataTestId}-Learn-More`}
                  />
                )}

                {buyNowLink?.url && buyNowLink?.name && (
                  <a
                    href={buyNowLink.url}
                    onClick={handleSelectItemEvent}
                    track-product-click="true"
                    data-product={trackingDataJson}
                    className="cta primary"
                    data-testid={`${dataTestId}-Buy-Now`}
                  >
                    {buyNowLink.name}
                  </a>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListingCard;
